import { Quill } from 'react-quill';
import type { SxProps, Theme } from '@mui/material';

/**
 * Shared helpers for a "Clear color" control in React-Quill toolbars.
 *
 * - Registers a custom `resetColor` toolbar icon (Material `format_color_reset`).
 * - Provides the toolbar handler that strips the applied text colour (so text
 *   falls back to the editor's default colour).
 * - Provides instant (no browser-delay) CSS tooltips for the colour controls.
 *
 * Importing this module registers the icon as a side effect.
 */

// Material `format_color_reset` icon.
const RESET_COLOR_ICON =
  '<svg viewBox="0 0 24 24" width="18" height="18">' +
  '<path fill="currentColor" d="M18 14c0-4-6-10.8-6-10.8s-1.34 1.51-2.75 3.55l8.59 8.59c.1-.43.16-.88.16-1.34zM3.27 5 2 6.27l3.51 3.51C5.19 10.53 5 11.29 5 12c0 3.87 3.13 7 7 7 1.36 0 2.62-.39 3.69-1.05L18.73 21 20 19.73 3.27 5z"/>' +
  '</svg>';

// Register the custom "reset colour" toolbar icon once. Buttons with class
// `ql-resetColor` in a Quill toolbar will render this icon.
const quillIcons = Quill.import('ui/icons') as Record<string, string>;
if (!quillIcons.resetColor) {
  quillIcons.resetColor = RESET_COLOR_ICON;
}

/**
 * Quill toolbar handler that clears the applied text colour. Bind under the
 * key `resetColor` in `modules.toolbar.handlers`. `this` is the toolbar module.
 */
export function resetColorHandler(this: any) {
  const quill = this.quill;
  const sel = quill.getSelection();
  if (sel && sel.length > 0) {
    quill.formatText(sel.index, sel.length, 'color', false);
  } else {
    // Nothing selected — reset the colour of the whole content.
    quill.formatText(0, quill.getLength(), 'color', false);
  }
}

/** sx that renders instant tooltips for elements carrying a `data-tooltip`. */
export const colorTooltipSx: SxProps<Theme> = {
  '& [data-tooltip]': { position: 'relative' },
  '& [data-tooltip]:hover::after': {
    content: 'attr(data-tooltip)',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#1F2937',
    color: '#fff',
    fontSize: '11px',
    lineHeight: 1.4,
    padding: '3px 8px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 2000,
  },
};

/**
 * Decorate the colour toolbar controls within `root`: ensure the custom
 * "Clear color" button shows its icon (in case the icon registry wasn't applied
 * when Quill built the toolbar) and attach instant tooltips to the colour controls.
 */
export function applyColorTooltips(root: HTMLElement | null) {
  if (!root) return;
  const resetBtn = root.querySelector('button.ql-resetColor');
  if (resetBtn) {
    resetBtn.setAttribute('data-tooltip', 'Clear color');
    if (!resetBtn.innerHTML.trim()) resetBtn.innerHTML = RESET_COLOR_ICON;
  }
  const setTip = (selector: string, tip: string) => {
    const el = root.querySelector(selector);
    if (el) el.setAttribute('data-tooltip', tip);
  };
  setTip('.ql-color.ql-picker .ql-picker-label', 'Text color');
  setTip('.ql-background.ql-picker .ql-picker-label', 'Background color');
}
